<template>
  <div
    v-click-outside="hideCalendarOverview"
    data-testid="daily-overview"
    :data-loading="isLoading"
    class="aula-calendar daily-overview"
    tabindex="0"
    :class="{ mobile: !isAside, 'daily-close': isAside }"
    @click="showCalendarOverview"
    @keydown.enter="showCalendarOverview"
    @focus="showCalendarOverview"
  >
    <div class="week-number">
      {{ $t('label.week_number', { number: weekNumber }) }}
    </div>
    <DailyOverviewCalendarView
      v-if="isProfileLoaded"
      ref="fullCalendar"
      :event-sources="fetchEvents"
      :resources="resources"
      @datesSet="handleDatesSet"
    />
    <b-btn
      v-if="!isMobile"
      variant="link"
      class="daily-overview-close"
      :aria-label="$getText('ARIA_LABEL_CLOSE_CALENDAR')"
      @click.stop="hideCalendarOverview()"
    >
      <i class="icon-Aula_close" aria-hidden="true" />
    </b-btn>
    <b-button variant="link" :class="!isMobile ? 'pull-right' : ''" size="sm" @click="goToCalendar">
      <span class="button-text">{{ $getText('DAILY_OVERVIEW_SEE_CALENDAR') }}</span
      ><i class="icon-Aula_forward-arrow" />
    </b-button>
    <important-date-overview
      v-if="isAside"
      @showCalendarOverview="showCalendarOverview"
      @hideCalendarOverview="hideCalendarOverview"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { mapGetters } from 'vuex';
import { types } from '../../store/types/types';
import moment from 'moment-timezone';
import ImportantDateOverview from './ImportantDateOverview.vue';
import { Cookie } from '../../../shared/assets/plugins/cookie';
import { moduleWidgetPlacements } from '../../../shared/enums/moduleWidgetPlacements';
import { moduleTypes } from '../../../shared/enums/moduleTypes';
import { portalRoles } from '../../../shared/enums/portalRoles';
import { docTypes } from '../../../shared/enums/docTypes';
import $ from 'jquery';
import DailyOverviewCalendarView from './DailyOverviewCalendarView.vue';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { useUserProfileStore } from '../../../shared/stores';

export default {
  components: {
    DailyOverviewCalendarView,
    'important-date-overview': ImportantDateOverview,
  },
  props: {
    isAside: {
      type: Boolean,
      default: true,
    },
  },
  data: function () {
    return {
      calendar: null,
      weekNumber: moment().isoWeek(),
      moment: moment,
      moduleWidgetPlacements: moduleWidgetPlacements,
      moduleTypes: moduleTypes,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      events: types.GET_EVENTS,
      institutions: types.GET_INSTITUTIONS,
      activeInstitutions: types.GET_ACTIVE_INSTITUTIONS,
      children: types.GET_CHILDREN,
      activeChildren: types.GET_ACTIVE_CHILDREN,
      menuItems: types.GET_MENU_ITEMS,
      isMobile: types.GET_IS_MOBILE,
      activeGroupHome: types.GET_ACTIVE_GROUP_HOME,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    isProfileLoaded() {
      return this.userProfileStore.isLoading === false;
    },
    defaultProfile() {
      return {
        role: this.profile.role,
        id: this.profile.id,
      };
    },
    resources() {
      let resourcesArray = [];
      if ([portalRoles.GUARDIAN, portalRoles.OTP].indexOf(this.profile.role) > -1) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        for (const child of this.children) {
          if (
            this.activeChildren.includes(child.id) &&
            resourcesArray.filter(c => c.profileId == child.profileId).length == 0
          ) {
            const resource = {
              id: docTypes.PROFILE.toLowerCase() + '-' + child.id,
              singleId: child.id,
              title: child.name,
              type: docTypes.PROFILE.toLowerCase(),
              eventClassName: child.id,
              shortName: child.shortName,
              profileId: child.profileId,
              profilePicture: child.profilePicture,
            };
            resourcesArray.push(resource);
          }
        }
      } else {
        resourcesArray = [
          {
            id: docTypes.PROFILE.toLowerCase() + '-' + this.profile.id,
            singleId: this.profile.id,
            type: docTypes.PROFILE.toLowerCase(),
            title: this.profile.firstName + ' ' + this.profile.lastName,
            shortName: this.profile.shortName,
            eventClassName: this.profile.id,
            profilePicture: this.profile.profilePicture,
            profileId: this.profile.profileId,
          },
        ];
      }
      return resourcesArray;
    },
  },
  methods: {
    ...mapActions({
      loadEventsByProfileId: types.LOAD_EVENTS_BY_PROFILEID,
      loadEventByGroupHome: types.LOAD_EVENTS_BY_GROUPHOME,
      clearEvents: types.CLEAR_EVENTS,
    }),
    handleDatesSet(dateInfo) {
      this.weekNumber = DateTimeUtil.formatDate(dateInfo.start, 'W');
    },
    showCalendarOverview() {
      if (!this.isMobile) {
        $('.daily-overview').removeClass('daily-close');
        $('.daily-overview').addClass('daily-open');
      }
    },
    shouldHideCalendarOverview(event) {
      if (this.isMobile) {
        return false;
      }

      if (event != null) {
        return !(this.isChildOfFullcalendarToolbar(event.target) || this.isChildOfCalendarOverivew(event.target));
      }

      return true;
    },
    isChildOfFullcalendarToolbar(target) {
      // when using today, prev and next in Fullcalendar toolbar, the target is referencing a shadow dom element.
      // A way to check if we are in the calendar toolbar is to see if the top parent has the class .fc-left
      const fullcalendarToolbarClass = 'fc-left';
      return this.isChildOfNodeWithClass(target, fullcalendarToolbarClass);
    },
    isChildOfCalendarOverivew(target) {
      const dailyOverviewContainer = this.$el;
      return dailyOverviewContainer.contains(target);
    },
    isChildOfNodeWithClass(target, className) {
      let node = target;

      while (node != null) {
        if (node.classList != null && node.classList.contains(className)) {
          return true;
        }
        node = node.parentNode;
      }
      return false;
    },
    hideCalendarOverview(event) {
      if (this.shouldHideCalendarOverview(event)) {
        $('.daily-overview').removeClass('daily-open');
        $('.daily-overview').addClass('daily-close');
      }
    },
    calculateResourcesAndEvents() {
      const eventsArray = [];
      for (const event of this.events) {
        event.startEditable = false;
        if (
          event.resourceIds != null &&
          this.resources.filter(resource => event.resourceIds.includes(String(resource.id))).length > 0 &&
          !event.type.includes('sum-')
        ) {
          for (const resourceId of event.resourceIds) {
            eventsArray.push({
              ...event,
              resourceIds: [resourceId],
              parentId: resourceId,
            });
          }
        }
      }
      return eventsArray;
    },
    fetchEvents(fetchInfo, successCallback) {
      this.isLoading = true;
      const finishLoading = events => {
        successCallback(events);
        this.isLoading = false;
      };
      const failLoading = () => {
        // Always clear loading — a rejected calendar fetch must not leave the widget stuck
        // with data-loading="true" (blocks Playwright OverviewPage.waitForReady).
        successCallback([]);
        this.isLoading = false;
      };

      if (this.profile.role === portalRoles.OTP) {
        if (this.activeGroupHome == null) {
          finishLoading([]);
          return Promise.resolve([]);
        }
        const groupHomeChildPairs = this.activeChildren.map(activeChildId => ({
          regardingChildId: activeChildId,
          groupHomeId: this.activeGroupHome.id,
        }));

        let instProfileIds = [this.defaultProfile.id];
        if ([portalRoles.OTP, portalRoles.GUARDIAN].indexOf(this.defaultProfile.role) > -1) {
          instProfileIds = instProfileIds.concat(this.activeChildren);
        }
        const params = {
          groupHomeChildPairs,
          start: moment(fetchInfo.start),
          end: moment(fetchInfo.end),
          instProfileIds,
        };
        return this.loadEventByGroupHome(params)
          .then(() => finishLoading(this.calculateResourcesAndEvents()))
          .catch(failLoading);
      }

      let instProfileIds = [];
      const activeInstitutions = this.institutions.filter(institution =>
        this.activeInstitutions.includes(institution.institutionCode)
      );
      for (const institution of activeInstitutions) {
        if (instProfileIds.filter(id => id == institution.institutionProfileId).length == 0) {
          instProfileIds.push(institution.institutionProfileId);
        }
      }
      if (this.profile.role === portalRoles.GUARDIAN) {
        instProfileIds = instProfileIds.concat(this.children.map(child => child.id));
      }
      this.clearEvents();
      return this.loadEventsByProfileId({
        instProfileIds: instProfileIds,
        start: moment(fetchInfo.start),
        end: moment(fetchInfo.end),
      })
        .then(() => finishLoading(this.calculateResourcesAndEvents()))
        .catch(failLoading);
    },
    goToCalendar() {
      const currentDate = moment(this.calendar.getDate()).format('YYYY-MM-DD');
      Cookie.Create('calendarDate', currentDate);
      this.$router.push({ name: 'calendar' });
    },
    init() {
      if (this.isProfileLoaded) {
        this.$nextTick(() => {
          this.clearEvents();
          this.calendar = this.$refs.fullCalendar.getApi();
        });
      }
      if (!Cookie.Read('initialLogin')) {
        this.showCalendarOverview();
        Cookie.Create('initialLogin', true);
        setTimeout(() => {
          this.hideCalendarOverview();
        }, 5000);
      }
    },
  },
  watch: {
    activeInstitutions() {
      if (this.calendar != null) {
        this.clearEvents();
        if (this.calendar != null) {
          this.calendar.refetchEvents();
        }
      }
    },
    isProfileLoaded() {
      this.init();
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;

.aula-calendar {
  background: white;
  @media only screen and (min-width: 992px) and (max-width: 1149px) {
    width: $layout-right-normal;
  }
  @media only screen and (min-width: 1150px) {
    width: $layout-right-wide;
  }
  height: 100%;

  &.daily-open {
    @media only screen and (min-width: 992px) and (max-width: 1149px) {
      right: $layout-right-normal;
    }
    @media only screen and (min-width: 1150px) {
      right: $layout-right-wide;
    }
    height: 100%;
  }
  &.daily-close {
    right: 60px;
    z-index: 10;
  }
  &.mobile {
    width: 100%;
    padding: 0;
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none; /* hide scrollbars in IE 10+ */
    &::-webkit-scrollbar {
      width: 0px; /* remove scrollbar space */
      background: transparent; /* optional: just make scrollbar invisible */
    }
  }
}

.week-number {
  position: absolute;
  top: 88px;
  left: 8px;
  z-index: 1;
}
.daily-overview-close {
  position: absolute;
  top: 24px;
  right: 4px;
  padding: 4px 8px;
}
.mobile {
  .week-number {
    top: 16px;
    left: 4px;
  }
}
.fc {
  ::v-deep .fc-view {
    .fc-timegrid-slot-label-cushion {
      padding: 0 8px;
    }
    .fc-event {
      cursor: default;
      &:hover {
        background-color: var(--fc-event-bg-color);
      }
    }
    &:not(.fc-dayGridMonth-view) .fc-daygrid-event:hover {
      background-color: var(--calendar-secondary);
    }
  }
}
</style>
